/** Shared validation rules for patient registration (frontend + mirrored on backend). */

export const REGISTRATION_MAX_AGE = 120;
export const REGISTRATION_NAME_MAX_LENGTH = 200;

export interface RegistrationFormValues {
  patient_name: string;
  gender: string;
  date_of_birth: string;
  age: string;
  date_of_registration?: string;
}

function parseIsoDate(value: string): Date | null {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function validatePatientName(name: string): string | null {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return 'Full Name is required';
  if (trimmed.length > REGISTRATION_NAME_MAX_LENGTH) {
    return `Full Name must be ${REGISTRATION_NAME_MAX_LENGTH} characters or fewer`;
  }
  if (!/[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]/.test(trimmed)) {
    return 'Full Name must contain letters; numbers or special characters only are not allowed';
  }
  return null;
}

export function validateGender(gender: string): string | null {
  const trimmed = String(gender ?? '').trim();
  if (!trimmed) return 'Gender is required';
  return null;
}

export function validateDateOfBirth(dob: string): string | null {
  const trimmed = String(dob ?? '').trim();
  if (!trimmed) return null;

  const parsed = parseIsoDate(trimmed);
  if (!parsed) return 'Please enter a valid date';

  const today = startOfToday();
  if (parsed > today) return 'Date of Birth cannot be a future date';

  const oldestAllowed = new Date(today);
  oldestAllowed.setFullYear(oldestAllowed.getFullYear() - REGISTRATION_MAX_AGE);
  if (parsed < oldestAllowed) return 'Please enter a valid date of birth';

  return null;
}

export function validateAge(age: string, hasDob: boolean): string | null {
  if (hasDob) return null;

  const trimmed = String(age ?? '').trim();
  if (!trimmed) return null;

  if (!/^\d+$/.test(trimmed)) return 'Age must be a numeric value';

  const n = Number(trimmed);
  if (n <= 0) return 'Age must be a positive number greater than zero';
  if (n > REGISTRATION_MAX_AGE) return 'Please enter a valid age';

  return null;
}

export function validateRegistrationForm(values: RegistrationFormValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const hasDob = String(values.date_of_birth ?? '').trim() !== '';

  const nameError = validatePatientName(values.patient_name);
  if (nameError) errors.patient_name = nameError;

  const genderError = validateGender(values.gender);
  if (genderError) errors.gender = genderError;

  const dobError = validateDateOfBirth(values.date_of_birth);
  if (dobError) errors.date_of_birth = dobError;

  const ageError = validateAge(values.age, hasDob);
  if (ageError) errors.age = ageError;

  return errors;
}

export function firstRegistrationError(errors: Record<string, string>): string | null {
  const keys = ['patient_name', 'gender', 'date_of_birth', 'age'];
  for (const key of keys) {
    if (errors[key]) return errors[key];
  }
  return null;
}
